import React from 'react';
import PropTypes from 'prop-types';
import { Table, TableHead, TableBody, TableRow, TableCell, TableFooter, IconButton, Tooltip, Box } from '@material-ui/core';
import { Edit, Delete, Print, MenuBook } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { formatCents } from '../../../../helpers/precision';
import { formatDate, formatNumber, calculateSums } from '../utils';
import { TAB_INDICES, METAL_IDS } from '../constants';
import MetalSelector from './MetalSelector';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    overflow: 'hidden',
    borderRadius: 20,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: 'none'
  },
  stickyHeaderBlock: {
    background: '#fff',
    padding: theme.spacing(2, 2, 0),
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    boxShadow: '0 2px 4px -2px rgba(0,0,0,0.08)',
    zIndex: 1
  },
  headTable: { 
    tableLayout: 'fixed', 
    width: '100%', 
    minWidth: 1120, 
    borderCollapse: 'separate', 
    borderSpacing: 0 
  },
  bodyScroll: { 
    flex: 1, 
    overflowY: 'auto', 
    overflowX: 'hidden', 
    position: 'relative' 
  },
  tableFixed: { 
    tableLayout: 'fixed', 
    width: '100%', 
    minWidth: 1120 
  },
  footerStickyBar: {
    position: 'sticky',
    bottom: 0,
    background: '#fff',
    boxShadow: '0 -2px 6px -2px rgba(0,0,0,0.08)',
    zIndex: 1
  },
  actionsCell: { whiteSpace: 'nowrap' },
  colDate: { width: 85 },
  colEntity: { width: 140 },
  colPerte: { width: 70 },
  colEntree: { width: 95 },
  colSortie: { width: 95 },
  colSortie750: { width: 95 },
  colPerteVal: { width: 95 },
  colSolde: { width: 95 },
  colNote: { width: 120 },
  colDesignation: { width: 150 },
  colActions: { width: 80 },
  designationCell: { 
    overflow: 'hidden', 
    textOverflow: 'ellipsis', 
    whiteSpace: 'nowrap' 
  },
  rowEditing: {
    position: 'relative',
    background: '#fff8e1 !important'
  },
  soldePositive: { color: '#1b7f36', fontWeight: 600 },
  soldeNegative: { color: '#c62828', fontWeight: 600 }
}));

/**
 * Table des mouvements avec en-tête, corps et pied de page
 */
const MovementsTable = ({
  rows,
  tab,
  selectedMetal,
  metalGroups,
  metauxList,
  onMetalChange,
  onEdit,
  onDelete,
  onPrint,
  onPrintPoliceBook,
  editRowId,
  editClalRowId,
  lastEditedRowId,
  bodyScrollHeight,
  headerBlockRef,
  totalPrixAchat
}) => {
  const classes = useStyles();
  
  const selectedMetalObj = metauxList.find(m => String(m.id_metal) === String(selectedMetal)) || {};
  const isSelectedMetalGold = String(selectedMetal) === METAL_IDS.OR;
  const selectedMetalLabel = selectedMetalObj.description || (isSelectedMetalGold ? 'Or' : `Métal ${selectedMetal}`);
  
  const sums = calculateSums(rows);
  const fmtC = (c) => (c === 0 || c ? formatCents(c) : '');

  const getEntityLabel = (row) => {
    if (tab === TAB_INDICES.CLAL) {
      if (row.__source === 'CLAL') return 'CLAL';
      return row.fournisseur_label || row.client_label || row.fournisseur_raison_soc || 
             row.fournisseur_id || row.client_id || row.__source;
    }
    
    if (tab === TAB_INDICES.FOURNISSEURS) {
      return row.fournisseur_label || row.fournisseur_raison_soc || row.fournisseur_id;
    }
    
    return row.client_label || row.client || row.client_id;
  };

  const calculatePerteValue = (row) => {
    const sortie750 = parseFloat(row.sortie_or_750 || 0) || 0;
    const sortieFinTh = sortie750 * 0.75;
    const pertePct = typeof row.perte === 'number' ? row.perte : Number(row.perte || 0);
    
    if (sortieFinTh > 0 && pertePct > 0) {
      const perteVal = sortieFinTh * (pertePct / 100);
      return perteVal.toFixed(2);
    }
    return '';
  };

  return (
    <div className={classes.tableContainer}>
      <div className={classes.stickyHeaderBlock} ref={headerBlockRef}>
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ flexWrap: 'wrap', gap: 12 }}>
          <MetalSelector
            metalGroups={metalGroups}
            selectedMetal={selectedMetal}
            onMetalChange={onMetalChange}
            metauxList={metauxList}
          />
          
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Box component="span" style={{ opacity: .7, fontWeight: 500, fontSize: 14 }}>
              Solde: {fmtC(sums.solde)}
            </Box>

            {tab === TAB_INDICES.CLAL && totalPrixAchat != null && (
              <Box component="span" style={{ fontWeight: 600, fontSize: 13, color: '#1a73e8', background: '#e8f0fe', borderRadius: 10, padding: '4px 10px' }}>
                Total achat : {Number(totalPrixAchat).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </Box>
            )}
            
            <Tooltip title="Imprimer la liste" placement="left">
              <IconButton size="small" onClick={onPrint}>
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Livre de police (tous métaux)" placement="left">
              <IconButton size="small" onClick={onPrintPoliceBook}>
                <MenuBook fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Table size="small" className={classes.headTable} style={{ minWidth: tab === TAB_INDICES.CLAL ? 1120 : (isSelectedMetalGold ? 1000 : 900) }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" className={classes.colDate}>Date</TableCell>
              <TableCell className={classes.colEntity}>
                {tab === TAB_INDICES.CLAL ? 'Source' : (tab === TAB_INDICES.FOURNISSEURS ? 'Fournisseur' : 'Client')}
              </TableCell>
              <TableCell align="center" className={classes.colPerte}>% Perte</TableCell>
              <TableCell className={classes.colDesignation}>Désignation</TableCell>
              {tab === TAB_INDICES.CLAL && <TableCell className={classes.colNote}>Note</TableCell>}
              <TableCell align="right" className={classes.colEntree}>
                Entrée {isSelectedMetalGold ? 'Or Fin' : 'Métal'}
              </TableCell>
              
              {isSelectedMetalGold ? (
                <>
                  {tab === TAB_INDICES.FOURNISSEURS && <TableCell align="right" className={classes.colSortie750}>Sortie Or 750</TableCell>}
                  {tab === TAB_INDICES.FOURNISSEURS && <TableCell align="right" className={classes.colSortie}>Sortie Or Fin</TableCell>}
                  {tab === TAB_INDICES.FOURNISSEURS && <TableCell align="right" className={classes.colPerteVal}>Perte Or Fin</TableCell>}
                  {tab !== TAB_INDICES.FOURNISSEURS && <TableCell align="right" className={classes.colSortie750}>Sortie Or 750</TableCell>}
                  {tab !== TAB_INDICES.FOURNISSEURS && <TableCell align="right" className={classes.colSortie}>Sortie Or Fin</TableCell>}
                  {tab !== TAB_INDICES.FOURNISSEURS && <TableCell align="right" className={classes.colPerteVal}>Perte Or Fin</TableCell>}
                </>
              ) : (
                <>
                  <TableCell align="right" className={classes.colSortie}>Sortie Métal</TableCell>
                  <TableCell align="right" className={classes.colPerteVal}>Perte Métal</TableCell>
                </>
              )}
              
              <TableCell align="right" className={classes.colSolde}>
                Solde {isSelectedMetalGold ? 'Or Fin' : 'Métal'}
              </TableCell>
              <TableCell align="right" className={classes.colActions}>Actions</TableCell>
            </TableRow>
          </TableHead>
        </Table>
      </div>
      
      <div className={classes.bodyScroll} style={bodyScrollHeight ? { height: bodyScrollHeight, overflowY: 'auto' } : { overflowY: 'auto', flex: 1 }}>
        <Table size="small" className={classes.tableFixed} style={{ minWidth: tab === TAB_INDICES.CLAL ? 1120 : (isSelectedMetalGold ? 1000 : 900) }} stickyHeader={false}>
          <TableBody>
            {rows.map((row, idx) => {
              const isEditing = (editRowId && editRowId === row.id) || (editClalRowId && row.__source === 'CLAL' && editClalRowId === row.id);
              const isHighlighted = lastEditedRowId && row.id === lastEditedRowId;
              
              const isRecentRow = () => {
                const rowDate = new Date(row.date_mouvement);
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                return rowDate >= oneWeekAgo;
              };

              return (
                <TableRow 
                  key={`mov-${selectedMetal}-${row.id}-${idx}`} 
                  hover 
                  className={(isEditing || isHighlighted) ? classes.rowEditing : undefined}
                >
                  <TableCell align="center" className={classes.colDate}>
                    {formatDate(row.date_mouvement)}
                  </TableCell>
                  <TableCell className={classes.colEntity}>
                    {getEntityLabel(row)}
                  </TableCell>
                  <TableCell align="center" className={classes.colPerte}>
                    {row.perte}
                  </TableCell>
                  <TableCell className={classes.colDesignation + ' ' + classes.designationCell}>
                    {row.designation}
                  </TableCell>
                  {tab === TAB_INDICES.CLAL && (
                    <TableCell className={classes.colNote + ' ' + classes.designationCell}>
                      {row.note || ''}
                    </TableCell>
                  )}
                  <TableCell align="right" className={classes.colEntree}>
                    {(tab === TAB_INDICES.CLAL && row.__source === 'FOUR') ? '' : formatNumber(row.entree_or_fin != null ? row.entree_or_fin : row.entree_fin)}
                  </TableCell>
                  
                  {isSelectedMetalGold ? (
                    <>
                      {tab === TAB_INDICES.FOURNISSEURS && (
                        <>
                          <TableCell align="right" className={classes.colSortie750}>
                            {formatNumber(row.sortie_or_750)}
                          </TableCell>
                          <TableCell align="right" className={classes.colSortie}>
                            {formatNumber(row.sortie_or_fin)}
                          </TableCell>
                          <TableCell align="right" className={classes.colPerteVal}>
                            {calculatePerteValue(row)}
                          </TableCell>
                        </>
                      )}
                      {tab !== TAB_INDICES.FOURNISSEURS && (
                        <>
                          <TableCell align="right" className={classes.colSortie750}>
                            {formatNumber(row.sortie_or_750)}
                          </TableCell>
                          <TableCell align="right" className={classes.colSortie}>
                            {fmtC(row.__sortieFinCents)}
                          </TableCell>
                          <TableCell align="right" className={classes.colPerteVal}>
                            {fmtC(row.__perteFinCents)}
                          </TableCell>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <TableCell align="right" className={classes.colSortie}>
                        {fmtC(row.__sortieFinCents)}
                      </TableCell>
                      <TableCell align="right" className={classes.colPerteVal}>
                        {fmtC(row.__perteFinCents)}
                      </TableCell>
                    </>
                  )}
                  
                  <TableCell 
                    align="right" 
                    className={row.__soldeMetalCents >= 0 ? classes.soldePositive + ' ' + classes.colSolde : classes.soldeNegative + ' ' + classes.colSolde}
                  >
                    {fmtC(row.__soldeMetalCents)}
                  </TableCell>
                  
                  <TableCell align="right" className={classes.actionsCell + ' ' + classes.colActions}>
                    {isRecentRow() && (
                      <>
                        <Tooltip title="Éditer" placement="top">
                          <IconButton size="small" onClick={() => onEdit(row)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer" placement="top">
                          <IconButton size="small" onClick={() => onDelete(row)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={isSelectedMetalGold ? 10 : 9} align="center">
                  Aucun mouvement
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          
          {rows.length > 0 && (
            <TableFooter>
              <TableRow className={classes.footerStickyBar}>
                <TableCell colSpan={tab === TAB_INDICES.CLAL ? 5 : 4} align="right" style={{ fontWeight: 600 }}>
                  Sous-total {selectedMetalLabel}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 600 }}>
                  {fmtC(sums.entree)}
                </TableCell>
                {isSelectedMetalGold && (
                  <TableCell align="right" style={{ fontWeight: 600 }}>
                    {fmtC(sums.sortie750)}
                  </TableCell>
                )}
                <TableCell align="right" style={{ fontWeight: 600 }}>
                  {fmtC(sums.sortie)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 600 }}>
                  {fmtC(sums.perte)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {fmtC(sums.solde)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

MovementsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  tab: PropTypes.number.isRequired,
  selectedMetal: PropTypes.string,
  metalGroups: PropTypes.object.isRequired,
  metauxList: PropTypes.array.isRequired,
  onMetalChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  onPrintPoliceBook: PropTypes.func.isRequired,
  editRowId: PropTypes.number,
  editClalRowId: PropTypes.number,
  lastEditedRowId: PropTypes.number,
  bodyScrollHeight: PropTypes.number,
  headerBlockRef: PropTypes.object,
  totalPrixAchat: PropTypes.number
};

export default MovementsTable;
